import React, { useState } from 'react';
import './KYCHelp.css';

const KYCHelp = () => {
  const [activeTab, setActiveTab] = useState('faq');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const faqs = [
    {
      id: 1,
      question: "What documents do I need for KYC verification?",
      answer: "You need: (1) PAN Card (mandatory), (2) Address proof (Aadhaar/Passport), (3) Cancelled cheque or bank statement, (4) GST certificate (if applicable), (5) Business registration documents (if applicable)."
    },
    {
      id: 2,
      question: "How long does KYC verification take?",
      answer: "KYC verification typically takes 24-48 hours once all documents are submitted. Real-time verifications like PAN and bank account happen instantly."
    },
    {
      id: 3,
      question: "What is penny drop verification?",
      answer: "Penny drop is a bank account verification method where we deposit a small amount (typically ₹1) to your account and verify the account details. This ensures your bank account is valid and active."
    },
    {
      id: 4,
      question: "Can I start accepting payments before KYC completion?",
      answer: "No, KYC completion is mandatory to start accepting payments. This is required by RBI regulations for all payment service providers."
    },
    {
      id: 5,
      question: "What if my KYC gets rejected?",
      answer: "If your KYC is rejected, you'll receive detailed feedback about the issues. You can then correct the problems and resubmit your documents for review."
    },
    {
      id: 6,
      question: "Is my data secure during KYC process?",
      answer: "Yes, we use bank-grade encryption and security measures. All data is stored securely and we are fully compliant with data protection regulations including GDPR."
    },
    {
      id: 7,
      question: "Can I update my KYC information later?",
      answer: "Yes, you can update your KYC information, but changes to critical details like PAN or bank account may require re-verification and could temporarily affect your services."
    },
    {
      id: 8,
      question: "What file formats are accepted for document upload?",
      answer: "We accept PDF, JPG, and PNG files. Maximum file size is 5MB per document. Ensure documents are clear and all text is readable."
    }
  ];

  const documentRequirements = [
    {
      type: "PAN Card",
      mandatory: true,
      description: "Permanent Account Number card issued by Income Tax Department",
      tips: [
        "Ensure PAN number is clearly visible",
        "Upload a clear, colored scan or photo",
        "Name on PAN should match business owner name"
      ]
    },
    {
      type: "Address Proof",
      mandatory: true,
      description: "Aadhaar Card, Passport, or Utility Bill (not older than 3 months)",
      tips: [
        "Address should match registered business address",
        "Utility bills should be recent (within 3 months)",
        "Ensure all text is clearly readable"
      ]
    },
    {
      type: "Bank Documents",
      mandatory: true,
      description: "Cancelled cheque or bank statement",
      tips: [
        "Cancelled cheque should have account holder name printed",
        "Bank statement should be recent (within 3 months)",
        "Account holder name should match business owner"
      ]
    },
    {
      type: "GST Certificate",
      mandatory: false,
      description: "GST registration certificate (if your business is GST registered)",
      tips: [
        "Required only if annual turnover > ₹20 lakhs",
        "GSTIN should be active and valid",
        "Business name should match across all documents"
      ]
    },
    {
      type: "Business Registration",
      mandatory: false,
      description: "Shop & Establishment license, Partnership deed, or Incorporation certificate",
      tips: [
        "Required for registered businesses",
        "Should be valid and not expired",
        "Business name should be consistent"
      ]
    }
  ];

  const troubleshooting = [
    {
      issue: "PAN verification failed",
      solutions: [
        "Check if PAN number is entered correctly (10 characters)",
        "Ensure PAN is active and not blocked",
        "Verify name spelling matches exactly with PAN card",
        "Contact support if PAN is valid but verification fails"
      ]
    },
    {
      issue: "Bank verification failed",
      solutions: [
        "Check IFSC code is correct (11 characters)",
        "Ensure account number is entered correctly",
        "Account holder name should match exactly",
        "Account should be active and operational"
      ]
    },
    {
      issue: "Document upload failed",
      solutions: [
        "Check file size is under 5MB",
        "Use supported formats: PDF, JPG, PNG",
        "Ensure stable internet connection",
        "Try uploading one document at a time"
      ]
    },
    {
      issue: "GST verification failed",
      solutions: [
        "Verify GSTIN is 15 characters long",
        "Check if GST registration is active",
        "Ensure GSTIN belongs to your business",
        "Contact GST helpline if registration is valid"
      ]
    }
  ];

  const toggleFAQ = (faqId) => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
  };

  return (
    <div className="kyc-help-container">
      <div className="help-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h2>KYC Help & Support</h2>
            <p>Everything you need to know about completing your KYC verification</p>
          </div>
          <button onClick={() => window.location.href = '/dashboard'}
            style={{ padding: '10px 20px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            ← Back to Dashboard
          </button>
        </div>
      </div>

      <div className="help-tabs">
        <button 
          className={`tab-button ${activeTab === 'faq' ? 'active' : ''}`}
          onClick={() => setActiveTab('faq')}
        >
          📋 FAQ
        </button>
        <button 
          className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          📄 Documents
        </button>
        <button 
          className={`tab-button ${activeTab === 'troubleshooting' ? 'active' : ''}`}
          onClick={() => setActiveTab('troubleshooting')}
        >
          🔧 Troubleshooting
        </button>
        <button 
          className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          📞 Contact
        </button>
      </div>

      <div className="help-content">
        {activeTab === 'faq' && (
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>
            <div className="faq-list">
              {faqs.map(faq => (
                <div key={faq.id} className="faq-item">
                  <button 
                    className="faq-question"
                    onClick={() => toggleFAQ(faq.id)}
                  >
                    <span>{faq.question}</span>
                    <span className={`faq-icon ${expandedFAQ === faq.id ? 'expanded' : ''}`}>
                      ▼
                    </span>
                  </button>
                  {expandedFAQ === faq.id && (
                    <div className="faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="documents-section">
            <h3>Document Requirements</h3>
            <div className="documents-list">
              {documentRequirements.map((doc, index) => (
                <div key={index} className="document-item">
                  <div className="document-header">
                    <h4>
                      {doc.type}
                      {doc.mandatory ? (
                        <span className="mandatory-badge">Required</span>
                      ) : (
                        <span className="optional-badge">Optional</span>
                      )}
                    </h4>
                  </div>
                  <p className="document-description">{doc.description}</p>
                  <div className="document-tips">
                    <h5>💡 Tips:</h5>
                    <ul>
                      {doc.tips.map((tip, tipIndex) => (
                        <li key={tipIndex}>{tip}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'troubleshooting' && (
          <div className="troubleshooting-section">
            <h3>Common Issues & Solutions</h3>
            <div className="troubleshooting-list">
              {troubleshooting.map((item, index) => (
                <div key={index} className="troubleshooting-item">
                  <h4>❗ {item.issue}</h4>
                  <div className="solutions">
                    <h5>Solutions:</h5>
                    <ul>
                      {item.solutions.map((solution, solutionIndex) => (
                        <li key={solutionIndex}>{solution}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="contact-section">
            <h3>Contact Support</h3>
            <div className="contact-options">
              <div className="contact-card">
                <div className="contact-icon">📧</div>
                <h4>Email Support</h4>
                <p>Get help via email</p>
                <a href="mailto:kyc-support@paymentgateway.com" className="contact-link">
                  kyc-support@paymentgateway.com
                </a>
                <small>Response within 24 hours</small>
              </div>
              
              <div className="contact-card">
                <div className="contact-icon">💬</div>
                <h4>Live Chat</h4>
                <p>Chat with our support team</p>
                <button className="contact-link btn-primary">
                  Start Chat
                </button>
                <small>Available 9 AM - 6 PM IST</small>
              </div>
              
              <div className="contact-card">
                <div className="contact-icon">📞</div>
                <h4>Phone Support</h4>
                <p>Speak with our KYC specialists</p>
                <a href="tel:+911800123456" className="contact-link">
                  +91 1800-123-456
                </a>
                <small>Mon-Fri, 9 AM - 6 PM IST</small>
              </div>
            </div>

            <div className="support-tips">
              <h4>📝 Before contacting support:</h4>
              <ul>
                <li>Have your merchant ID ready</li>
                <li>Describe the specific issue you're facing</li>
                <li>Mention any error messages you received</li>
                <li>Include screenshots if applicable</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KYCHelp;